import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useLocation, matchPath } from "react-router-dom";
import { getAllMenuItems } from "../menu/menuConfig";
import useDocumentTitle from "./useDocumentTitle";

// A page's main title. Pass `title` to set it explicitly (e.g. a saved query's own name); otherwise it
// is the current route's menu label. Renders nothing when neither applies — detail pages (a diamond, an
// image) have no menu label and no title of their own.
//
// `actions` are the buttons that belong to the page as a whole. They are laid out here rather than by
// each page so that they sit in the same place on every screen: at the far right of the title's line.
const PageTitle = ({
  title,
  actions,
}: {
  title?: string;
  actions?: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const label =
    title ??
    getAllMenuItems().find((item) => matchPath(item.path, pathname))?.label;

  // The tab says what the page says. A page about a particular stone or colour sets its own title
  // instead, with useDocumentTitle.
  useDocumentTitle(title);

  if (!label && !actions) return null;

  // Without actions the heading is left exactly as it was, so the pages that only have a title are
  // untouched by this.
  if (!actions) {
    return (
      <Typography variant="h5" sx={{ mb: 1 }}>
        {label}
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1 }}
    >
      {/* An empty box where a page has no title of its own, so the actions still end up right. */}
      {label ? <Typography variant="h5">{label}</Typography> : <Box />}
      {actions}
    </Stack>
  );
};

export default PageTitle;
